<template>
  <v-card 
    :color="activeStep === step.step ? 'blue-grey lighten-5': ''">
    <v-card-title primary-title>
      <h3 :class="['headline', !(activeStep === step.itemOrder) ? 'grey--text text--darken-1' : 'green--text text--darken-1']">
        {{ step.name }} 
        <span 
          v-if="step.lastUpdate"
          class="caption">Updated {{ step.lastUpdate.seconds | moment("from") }}</span>
      </h3>
      <v-spacer/>
      <v-dialog 
        v-if="step.type === userType && step.itemOrder <= activeStep && !locked"
        v-model="dialog" 
        hide-overlay
        max-width="1200"
        transition="scale-transition"
        scrollable>
        <v-btn
          slot="activator"
          :small="activeStep === step.itemOrder ? false : true"
          :color="activeStep === step.itemOrder ?'teal darken-1' : 'secondary'"
          fab
          dark       
        >
          <v-icon>{{ activeStep === step.itemOrder ? 'add' : 'edit' }}</v-icon>
        </v-btn>          
        <v-card>          
          <v-toolbar 
            card 
            dark 
            color="primary">
            <v-toolbar-title>{{ step.name }}</v-toolbar-title>         
          </v-toolbar>
          <v-card-text>
            <v-flex 
              v-if="formData"
              xs12 
            >
              <v-form 
                ref="form"
                v-model="valid">
                <div 
                  v-for="(block, index) in formData" 
                  :key="index"
                  lazy-validation>
                  <div
                    v-if="block.type == 'htmlblock'"
                    v-html="block.html"/>
                  <v-flex 
                    v-if="block.type == 'inputblock'" 
                    :md8="!(block.maxSize > 100)"
                    :offset-md2="!(block.maxSize > 100)">
                    <v-textarea
                      v-if="block.maxSize > 100"
                      v-model="block.value"
                      :hint="block.hint"
                      :persistent-hint="block.persistantHint"
                      :label="block.label"
                      :rules="[
                        v => !block.required || !!v || 'Required',
                        v => !v || v.length > block.minSize || `Must be ${block.minSize} or more characters`,
                        v => !v || v.length < block.maxSize || 'Must be less than ' + block.maxSize + ' characters'
                      ]"
                      :required="block.required"   
                    />
                    <v-text-field
                      v-else
                      v-model="block.value"
                      :hint="block.hint"
                      :persistent-hint="block.persistantHint"
                      :label="block.label"
                      :rules="[
                        v => !block.required || !!v || 'Required',
                        v => !v || v.length > block.minSize || `Must be ${block.minSize} or more characters`,
                        v => !v || v.length < block.maxSize || 'Must be less than ' + block.maxSize + ' characters'
                      ]"
                      :required="block.required"                           
                    />
                  </v-flex>
                  <v-flex 
                    v-if="block.type == 'numberblock'"
                    md8 
                    offset-md2>
                    <v-text-field               
                      v-model="block.value"          
                      :label="block.label"
                      :value="block.value" 
                      :hint="block.hint"
                      :persistent-hint="block.persistantHint"
                      :required="block.required"  
                      type="number"  
                      :min="block.min ? block.min : false"
                      :max="block.max ? block.max : false"
                      :rules="[
                        v => !block.required || !!v || 'Value required',
                        v => !block.min || Number(v) >= Number(block.min) || `Number must be ${block.min} or more`,
                        v => !block.max || Number(v) <= Number(block.max) || `Number must be less than ${block.max}`,
                        v => !block.integer || Number.isInteger(+v) || `Number must be an integer`
                      ]"   
                    />
                  </v-flex>
                  <v-flex 
                    v-if="block.type == 'selectblock'"
                    md8 
                    offset-md2>
                    <v-select 
                      v-model="block.value"              
                      :label="block.label" 
                      :items="block.options"   
                      :hint="block.hint"
                      :persistent-hint="block.persistantHint" 
                      :required="block.required" 
                      :rules="[v => !block.required || !!v || 'Required']"
                    />
                  </v-flex>
                  <v-flex v-if="block.type == 'standardblock'">
                    <div v-html="block.html"/>
                    <v-radio-group 
                      v-model="block.value" 
                      :required="block.required" 
                      :rules="[v => !block.required || !!Number.isInteger(v) || 'Required']"
                      row>
                      <v-radio 
                        v-for="(opt, index) in block.options"
                        :key="opt.label"
                        :label="opt.label"
                        :color="opt.value" 
                        :value="index" />                  
                    </v-radio-group>
                  </v-flex>
                  <v-flex 
                    v-if="block.type == 'fileblock'"
                    md8 
                    offset-md2>
                    <v-btn 
                      raised 
                      class="primary" 
                      @click="$refs[index][0].click()">{{ block.label }}</v-btn>
                    <input
                      :ref="index"
                      :accept="block.fileType"
                      type="file"
                      style="display: none"
                      @change="onFilePicked($event, block, index)"> 
                    <v-text-field
                      v-model="block.value"
                      :rules="[v => !block.required || !!v || 'Required']"
                    />
                    <v-progress-linear v-model="block.progress"/>
                  </v-flex>   
                  <v-flex 
                    v-if="block.type == 'sigblock'"
                    md8 
                    offset-md2>
                    <v-card-title>{{ block.label }}</v-card-title>
                    <v-img 
                      v-if="block.value"
                      :src="block.url" 
                      :alt="block.value"/>
                    <VueSignaturePad
                      v-if="!block.value"
                      class="signature"
                      width="100%"
                      height="300px"
                      ref="signaturePad"
                    />

                    <v-card-actions class="justify-center">
                      <v-btn
                        primary
                        @click.native="clearSig(block, index)"
                      >
                        Clear
                      </v-btn>

                      <v-btn
                        v-if="!block.value"
                        primary
                        @click.native="saveSig(block, index)">
                        Save
                      </v-btn>
                    </v-card-actions> 
                    <v-text-field
                      v-model="block.value"
                      readonly
                      :rules="[v => !block.required || !!v || 'Required']"
                    />
                  </v-flex> 
                  <v-flex 
                    v-if="block.type == 'dateblock'"
                    md8 
                    offset-md2>
                    <v-menu
                      v-model="block.menu"
                      :ref="block.label"
                      :close-on-content-click="false"
                      max-width="290"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          :value="block.value ? $moment(block.value).format('dddd, MMMM Do YYYY'):''"
                          clearable
                          :label="block.label"
                          readonly
                          :rules="[
                          v => !block.required || !!v || 'Required' ]"
                          v-on="on"
                          :hint="block.hint"
                          :persistent-hint="block.persistantHint"
                          :required="block.required" 
                          prepend-icon="event"
                        />
                      </template>
                      <v-date-picker
                        v-model="block.value"
                        @change="block.menu= false"
                      />
                    </v-menu>   
                  </v-flex>
                                           
                </div> 
              </v-form>                      
            </v-flex>
          </v-card-text>
          <v-divider/>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              color="primary" 
              flat 
              @click.native="close">
              Close
            </v-btn>
            <v-btn 
              color="primary" 
              flat 
              @click.native="save()">Save</v-btn>
          </v-card-actions>
        </v-card>          
      </v-dialog>
    </v-card-title>
    <v-card-text v-if="step.itemOrder < activeStep">     
      <v-alert
        v-if="blockResult"
        value="blockResult"
        :type="blockResult && blockResult.type"
      >
        {{ blockResult.message }}
      </v-alert> 
      <div 
        v-for="(block, index) in step.formData" 
        :key="index"
        lazy-validation>
        <div v-if="block.show && block.value !== null && block.value !== undefined && block.value !== '' ">        
          <v-card 
            v-if="block.type == 'htmlblock'" 
            flat>
            <v-card-text v-html="block.html"/>
          </v-card>
          <v-card 
            v-if="block.type == 'inputblock' && !(block.hide && block.hide.includes(userType))" 
            flat>
            <v-card-title 
              primary-title 
              class="subheading" >
              {{ block.label }}
            </v-card-title>
            <v-card-text 
              class="show-white-space grey lighten-4"
              v-text="block.value"/>
          </v-card>
          <v-card 
            v-if="block.type == 'dateblock' && !(block.hide && block.hide.includes(userType))" 
            flat>
            <v-card-title 
              primary-title 
              class="subheading" >
              {{ block.label }}
            </v-card-title>
            <v-card-text 
              class="show-white-space grey lighten-4"
              v-text="block.value ? $moment(block.value).format('dddd, MMMM Do YYYY'):''"/>
          </v-card>
          <v-card 
            v-if="block.type == 'numberblock' && !(block.hide && block.hide.includes(userType))" 
            flat>
            <v-card-title>
              <v-flex 
                md9 
                xs12>
                <div 
                  class="subheading" 
                  v-html="block.label"/>
              </v-flex>
              <v-flex 
                md3
                xs12 
                class="text-xs-right">
                <v-chip 
                  color="grey" 
                  text-color="white">
                  {{ block.value }}
                </v-chip>
              </v-flex>
            </v-card-title>
          </v-card>  
          <v-card 
            v-if="block.type == 'selectblock'" 
            flat>
            <v-card-title>
              <v-flex 
                md9 
                xs12>
                <div 
                  class="subheading" 
                  v-html="block.label"/>
              </v-flex>
              <v-flex 
                md3
                xs12
                class="text-xs-right">
                <v-chip 
                  color="grey" 
                  text-color="white">
                  {{ block.value }}
                </v-chip>
              </v-flex>
            </v-card-title>
          </v-card>                         
          <v-card 
            v-if="block.type == 'standardblock'" 
            flat>
            <v-card-title>
              <v-flex 
                md9 
                xs12>
                <div 
                  class="subheading" 
                  v-html="block.html"/>
              </v-flex>
              <v-flex 
                md3
                xs12 
                class="text-xs-right">
                <v-chip 
                  :color="block.options[block.value].value" 
                  text-color="white">
                  {{ block.options[block.value].label }}
                </v-chip>
              </v-flex>             
            </v-card-title>
          </v-card> 
          <v-card 
            v-if="block.type == 'fileblock'" 
            flat>
            <v-card-text>
              <v-btn 
                :href="block.url" 
                target="_blank"
                color="success">{{ block.value }}</v-btn>
            </v-card-text>
          </v-card>
          <v-card 
            v-if="block.type == 'sigblock'" 
            flat>
            <v-card-text center-text>
              <v-flex 
                xs12 
                sm8 
                lg6>              
                <v-img
                  :src="block.url" 
                  :alt="block.value"/>
              </v-flex>
            </v-card-text>
          </v-card>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import VueSignaturePad from "vue-signature-pad";

export default {
  name: "Steps",
  props: {
    studentUid: {
      type: String,
      default: null
    },
    placementKey: {
      type: String,
      default: null
    },
    userType: {
      type: String,
      default: null
    },
    step: {
      type: Object,
      default: null
    },
    activeStep: {
      type: Number,
      default: null
    },
    nextStep: {
      type: Object,
      default: null
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  components: {
    VueSignaturePad
  },
  data: () => ({
    dialog: false,
    valid: false,
    studenStep: null,
    file: {},
    filename: null,
    formData: []
  }),
  created() {
    if (this.step.formData) {
      this.formData = JSON.parse(JSON.stringify(this.step.formData));
    }
  },
  watch: {
    dialog: function(value) {
      if (value) {
        this.$nextTick(function() {
          console.log("resize canvas");
          this.$refs.signaturePad[0].resizeCanvas();
        });
      }
    }
  },
  computed: {
    user() {
      return this.$store.getters.user;
    },
    blockResult() {
      let result = null;
      if (this.step.formData) {
        this.step.formData.forEach(function(field) {
          if (field.type === "standardblock" && parseInt(field.value) >= 0) {
            if (field.options[field.value].value === "error") {
              result = {
                type: "error",
                message: "Requirements not met."
              };
            }
            if (!result && field.options[field.value].value === "warning") {
              result = {
                type: "warning",
                message: "Trouble meeting requirements."
              };
            }
          }
        });
      }
      return result;
    }
  },
  methods: {
    clearSig(block, index) {
      block.value = null;
      console.log(this.$refs.signaturePad[0]);
      this.$refs.signaturePad[0].clearSignature();
    },
    saveSig(block, index) {
      let self = this;
      console.log(this.$refs.signaturePad[0]);
      let sig = this.$refs.signaturePad[0].saveSignature();
      let sigData = sig.data.split(/,(.+)/)[1];
      console.log(sigData);

      let metadata = {
        contentType: "image/png"
      };
      console.log(index);
      let timestamp = +new Date();

      let uploadTask = this.$store.getters.st
        .ref()
        .child("users/" + self.user.uid + "/" + timestamp + "-signature.png")
        .putString(sigData, "base64", metadata);
      uploadTask.on(
        "state_changed",
        function(snapshot) {
          var progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          block.progress = progress;
          /*self.$store.dispatch("sendNotification", {
            snackbar: true,
            color: "deep-orange",
            text: "Upload is " + progress + "% done"
          });*/
        },
        function(error) {
          console.log(error);
        },
        function() {
          uploadTask.snapshot.ref.getDownloadURL().then(function(downloadURL) {
            console.log("File available at", downloadURL);
            block.url = downloadURL;
            block.value = "signature saved";
          });
        }
      );
    },
    /* Can this be re-written for FirestoreUploadAdapter */
    onFilePicked(event, block, index) {
      let self = this;
      const files = event.target.files;

      let ext = files[0].name.slice(files[0].name.lastIndexOf("."));

      let timestamp = new Date();
      let uploadTask = this.$store.getters.st
        .ref()
        .child("users/" + self.user.uid + "/" + timestamp + "-" + index + ext)
        .put(files[0]);
      uploadTask.on(
        "state_changed",
        function(snapshot) {
          var progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log("Upload is " + progress + "% done");
          block.progress = progress;
          /*self.$store.dispatch("sendNotification", {
            snackbar: true,
            color: "deep-orange",
            text: "Upload is " + progress + "% done"
          });*/
        },
        function(error) {
          console.log(error);
        },
        function() {
          uploadTask.snapshot.ref.getDownloadURL().then(function(downloadURL) {
            console.log("File available at", downloadURL);
            block.url = downloadURL;
            block.value = files[0].name;
          });
        }
      );
    },
    save() {
      //let self = this;

      if (this.$refs.form.validate()) {
        this.$store.dispatch("setLoading", true);
        // Get a new write batch
        let batch = this.$store.getters.db.batch();
        let d = new Date();

        let updatePlacement = {
          lastUpdate: d
        };
        // Check if the next step has been reached
        if (this.activeStep === this.step.itemOrder) {
          updatePlacement.step = this.step.itemOrder + 1;
          updatePlacement.nextStep = this.nextStep.name;
          updatePlacement.nextStepKey = this.nextStep[".key"];
          updatePlacement.nextStepType = this.nextStep.type;
          // Why don't I include nextstep key?

          /* Move the Locked to the function */
          if (this.nextStep.type === "Completed") {
            let n = d.getFullYear();
            updatePlacement.locked = n;
          }
        }
        // Check step for changes to Standards
        let standardResults = {};
        standardResults[`step${this.step.itemOrder}`] = {
          success: 0,
          warning: 0,
          error: 0
        };
        if (this.formData) {
          this.formData.forEach(element => {
            if (
              element.type === "standardblock" &&
              Number.isInteger(element.value)
            ) {
              standardResults[`step${this.step.itemOrder}`][
                element.options[element.value].value
              ] += 1;
            }
          });
        }

        let refPlacement = this.$store.getters.db
          .collection("students")
          .doc(this.studentUid)
          .collection("placements")
          .doc(this.placementKey);
        // batch.update(refPlacement, updatePlacement);
        batch.set(
          refPlacement,
          { ...updatePlacement, standardResults: standardResults },
          { merge: true }
        );

        let formData = this.formData;

        let refStep = this.$store.getters.db
          .collection("students")
          .doc(this.studentUid)
          .collection("placements")
          .doc(this.placementKey)
          .collection("steps")
          .doc(this.step[".key"]);
        // now adding Last Update to step
        batch.update(refStep, { formData, lastUpdate: d });

        batch
          .commit()
          .then(() => {
            console.log("batched it");
            this.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "cyan darken-2",
              text: this.step.name + " updated"
            });
            this.dialog = false;
            this.$store.dispatch("setLoading", false);
          })
          .catch(function(err) {
            console.log(err);
          });
      }
    },
    close() {
      this.$store.dispatch("sendNotification", {
        snackbar: true,
        color: "red darken-2",
        text: this.step.name + " not saved"
      });
      this.dialog = false;
    }
  }
};
</script>

<style>
.show-white-space {
  white-space: pre-wrap;
}
.signature {
  border: double 3px transparent;
  border-radius: 5px;
  background-image: linear-gradient(white, white),
    radial-gradient(circle at top left, #4bc5e8, #9f6274);
  background-origin: border-box;
  background-clip: content-box, border-box;
}
</style>
